import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Download, X, ChevronLeft, ChevronRight, Play, Pause, MessageSquare } from "lucide-react";
import { GoldRule, ChapterEyebrow } from "@/components/editorial";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROMAN = ["", "I","II","III","IV","V","VI","VII","VIII","IX","X",
  "XI","XII","XIII","XIV","XV","XVI","XVII","XVIII","XIX","XX",
  "XXI","XXII","XXIII","XXIV","XXV"];
const pad = (n) => String(n).padStart(2, "0");
const roman = (n) => (n > 0 && n < ROMAN.length ? ROMAN[n] : String(n));

/**
 * PremiumLightbox — Phase 3 editorial lightbox.
 * Same external props as the legacy lightbox so the orchestrator wiring stays
 * intact: photo, isFav, comment, setComment, onSaveComment, onToggleFav,
 * onClose, onPrev, onNext, mode (proofing|delivery), downloadsAvailable,
 * onDownload, idx, total, isSlideshow, toggleSlideshow, slideshowDuration,
 * watermark.
 */
export default function PremiumLightbox({
  photo,
  isFav,
  comment,
  setComment,
  onSaveComment,
  onToggleFav,
  onClose,
  onPrev,
  onNext,
  mode,
  downloadsAvailable,
  onDownload,
  idx,
  total,
  prevPhotoUrl,
  nextPhotoUrl,
  isSlideshow,
  toggleSlideshow,
  slideshowDuration = 4500,
  watermark,
  interactive,
}) {
  const containerRef = useRef(null);
  const lastFocusRef = useRef(null);

  // Trap focus + restore on close
  useEffect(() => {
    lastFocusRef.current = document.activeElement;
    containerRef.current?.focus();
    return () => {
      try { lastFocusRef.current?.focus(); } catch (_) {}
    };
  }, []);

  // Pause Lenis smooth-scroll while open
  useEffect(() => {
    const html = document.documentElement;
    const hadLenis = html.classList.contains("lenis-active");
    if (hadLenis) html.classList.add("lenis-paused");
    document.body.style.overflow = "hidden";
    return () => {
      if (hadLenis) html.classList.remove("lenis-paused");
      document.body.style.overflow = "";
    };
  }, []);

  // Keyboard: ← → Esc F
  useEffect(() => {
    function onKey(e) {
      const inField = ["INPUT", "TEXTAREA"].includes((e.target?.tagName || "").toUpperCase());
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if (inField) return;
      if (e.key === "ArrowLeft")  { e.preventDefault(); onPrev?.(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); onNext?.(); }
      else if (e.key.toLowerCase() === "f" && mode === "proofing") { e.preventDefault(); onToggleFav?.(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, onToggleFav, mode]);

  // Slideshow auto-advance
  useEffect(() => {
    if (!isSlideshow) return;
    const t = setInterval(() => onNext?.(), slideshowDuration);
    return () => clearInterval(t);
  }, [isSlideshow, slideshowDuration, onNext]);

  // Double-tap to favorite
  const lastTapRef = useRef(0);
  function handleTap() {
    if (mode !== "proofing") return;
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      onToggleFav?.();
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }

  // Lightbox = high-detail viewing but not raw original. 2000px @ q88 is
  // indistinguishable from the raw file on a 27" 4K monitor while being
  // 4-8× smaller on the wire. Client can still download the RAW original
  // via the "Download" button which hits a separate signed URL. External
  // (http-prefixed) URLs are left untouched.
  const photoSrc = photo?.url
    ? (photo.url.startsWith("http")
        ? photo.url
        : `${BACKEND}${photo.url}${photo.url.includes("?") ? "&" : "?"}w=2000&q=88`)
    : "";

  // Neighbor prefetch — silently warm the SW image cache for photos
  // adjacent to the current view so a client's next arrow-key / swipe
  // feels instant. Uses `Image()` (not `<link rel=prefetch>`) so the
  // fetch is intercepted by the Service Worker's `/api/files/…?w=` cache
  // handler and stored persistently across visits. `same-origin` external
  // URLs and empty neighbors are skipped.
  const _buildLightboxSrc = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${BACKEND}${url}${url.includes("?") ? "&" : "?"}w=2000&q=88`;
  };
  useEffect(() => {
    const targets = [
      _buildLightboxSrc(nextPhotoUrl),
      _buildLightboxSrc(prevPhotoUrl),
    ].filter(Boolean);
    // Kick off prefetches on the next tick so the current photo's load
    // isn't slowed by the neighbors sharing the network stack.
    const timer = setTimeout(() => {
      targets.forEach((src) => {
        const img = new Image();
        img.decoding = "async";
        img.src = src;
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [prevPhotoUrl, nextPhotoUrl]);

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-50 outline-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      style={{ background: "#0A0A09" }}
    >
      {/* Warm gold radial glow centered behind the photo */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at center, rgba(201,169,97,0.08) 0%, transparent 60%)" }} />

      {/* TOP-LEFT — frame counter */}
      <div className="absolute top-5 left-5 lg:top-8 lg:left-10 z-10 select-none">
        <ChapterEyebrow
          label={`${pad(idx + 1)} / ${pad(total)} — Frame ${roman(idx + 1)}`}
          tone="gold"
        />
        <GoldRule width="2rem" className="mt-2" />
      </div>

      {/* TOP-RIGHT — close + slideshow */}
      <div className="absolute top-5 right-5 lg:top-8 lg:right-10 z-10 flex items-center gap-3">
        {toggleSlideshow ? (
          <button
            onClick={toggleSlideshow}
            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[color:var(--gold-foil)]/55 text-[color:var(--cream)] hover:bg-[color:var(--gold-foil)]/15 transition-colors"
            aria-label={isSlideshow ? "Pause slideshow" : "Start slideshow"}
          >
            {isSlideshow ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        ) : null}
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          aria-label="Close viewer"
          className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[color:var(--gold-foil)]/70 text-[color:var(--cream)] hover:bg-[color:var(--gold-foil)]/15 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* PHOTO STAGE */}
      <div className="absolute inset-0 flex items-center justify-center px-4 lg:px-24 pt-24 pb-44">
        {/* Prev */}
        <button
          onClick={onPrev}
          aria-label="Previous photo"
          className="absolute left-3 lg:left-8 top-1/2 -translate-y-1/2 z-10 group inline-flex flex-col items-center gap-1 text-[color:var(--cream)]/80 hover:text-[color:var(--gold-foil)] transition-colors opacity-95 lg:opacity-0 lg:group-hover:opacity-100"
        >
          <ChevronLeft className="w-9 h-9" strokeWidth={1} />
          <span className="font-serif italic text-xs">prev</span>
        </button>

        <AnimatePresence mode="wait">
          {photo ? (
            <motion.div
              key={photo.id || idx}
              layoutId={`gallery-photo-${photo.id || idx}`}
              onClick={handleTap}
              className="relative cursor-zoom-in select-none flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              style={{ maxWidth: "80vw", maxHeight: "70vh" }}
            >
              <img
                src={photoSrc}
                alt={photo.filename || `Frame ${idx + 1}`}
                className="max-w-[80vw] max-h-[70vh] object-contain"
                draggable={false}
              />
              {watermark ? (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-white/12 text-5xl font-light tracking-[0.4em] uppercase rotate-[-15deg]">
                  proof
                </div>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Next */}
        <button
          onClick={onNext}
          aria-label="Next photo"
          className="absolute right-3 lg:right-8 top-1/2 -translate-y-1/2 z-10 group inline-flex flex-col items-center gap-1 text-[color:var(--cream)]/80 hover:text-[color:var(--gold-foil)] transition-colors opacity-95 lg:opacity-0 lg:group-hover:opacity-100"
        >
          <ChevronRight className="w-9 h-9" strokeWidth={1} />
          <span className="font-serif italic text-xs">next</span>
        </button>
      </div>

      {/* BOTTOM ACTION BAND */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-5 lg:px-12 pb-8 pt-6"
        style={{ background: "linear-gradient(to top, rgba(10,10,9,0.95), rgba(10,10,9,0))" }}>
        <div className="max-w-4xl mx-auto">
          {mode === "proofing" ? (
            <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
              {/* Heart */}
              <button
                onClick={onToggleFav}
                data-testid="lightbox-fav-btn"
                className="group inline-flex items-center gap-3 self-start lg:self-auto"
                aria-pressed={!!isFav}
              >
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${isFav ? "border-[color:var(--gold-base)] bg-[color:var(--gold-base)]/15" : "border-[color:var(--gold-foil)]/55"}`}>
                  <Heart className={`w-4 h-4 ${isFav ? "fill-[color:var(--gold-base)] text-[color:var(--gold-base)]" : "text-[color:var(--gold-foil)]"}`} strokeWidth={1.2} />
                </span>
                <span className="font-serif italic text-lg text-[color:var(--cream)]">{isFav ? "Favorited" : "Favorite for editing"}</span>
              </button>

              {/* Interactive: small "picked by" caption alongside the heart */}
              {interactive?.enabled && interactive.count > 0 && (
                <div className="hidden sm:flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--cream)]/70" data-testid="lightbox-interactive-picks">
                  <span className="text-[color:var(--gold-base)]">♥ {interactive.count}</span>
                  <span className="opacity-60">·</span>
                  <span className="normal-case tracking-normal text-xs italic text-[color:var(--cream)]/80">
                    {interactive.collaborators.slice(0, 3).map((c) => c.display_name).join(", ")}
                    {interactive.collaborators.length > 3 && ` +${interactive.collaborators.length - 3}`}
                  </span>
                </div>
              )}

              {/* G3: Comments trigger (interactive only) */}
              {interactive?.enabled && (
                <button
                  type="button"
                  onClick={interactive.onOpenComments}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[color:var(--gold-foil)]/40 hover:border-[color:var(--gold-base)] text-[color:var(--cream)] text-xs uppercase tracking-[0.22em] transition-colors"
                  data-testid="lightbox-comments-btn"
                  aria-label={`Open comments${interactive.commentCount ? ` (${interactive.commentCount})` : ""}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span>Comments</span>
                  {interactive.commentCount > 0 && (
                    <span className="text-[color:var(--gold-base)]" data-testid="lightbox-comments-count">{interactive.commentCount}</span>
                  )}
                </button>
              )}

              {/* Note input */}
              <input
                type="text"
                value={comment || ""}
                onChange={(e) => setComment?.(e.target.value)}
                placeholder="Optional note — e.g. crop tighter, brighten skin"
                data-testid="lightbox-comment-input"
                className="flex-1 bg-transparent border-0 border-b border-[color:var(--cream)]/30 focus:border-[color:var(--gold-foil)] py-2 outline-none text-[color:var(--cream)] placeholder:text-[color:var(--cream)]/40 placeholder:italic placeholder:font-serif text-base transition-colors"
              />

              {/* Save note link */}
              <button
                onClick={onSaveComment}
                data-testid="lightbox-save-comment-btn"
                className="ed-eyebrow text-[color:var(--gold-foil)] hover:text-[color:var(--gold-base)] transition-colors whitespace-nowrap inline-flex items-center gap-2 self-start lg:self-auto"
              >
                Save note <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : null}

          {mode === "delivery" && downloadsAvailable ? (
            <div className="flex items-center justify-between gap-3">
              <div className="font-serif italic text-[color:var(--cream)]/80 text-base truncate">{photo?.filename}</div>
              <button
                onClick={onDownload}
                data-testid="lightbox-dl-btn"
                className="inline-flex items-center gap-2 ed-eyebrow text-[color:var(--gold-foil)] hover:text-[color:var(--gold-base)] transition-colors"
              >
                <Download className="w-4 h-4" strokeWidth={1.2} /> Download
              </button>
            </div>
          ) : null}

          {/* Instruction hint */}
          <p className="mt-5 text-center text-[color:var(--cream)]/45 text-[11px] tracking-[0.2em] uppercase">
            Double-tap photo · F key · or tap the heart
          </p>
        </div>
      </div>
    </motion.div>
  );
}
